import { createSlice } from '@reduxjs/toolkit'
import { SettingsAnswerEmptyValue, SettingsName } from 'Constants/Settings'
import {
  generateRawAnswerFromEmptyValue,
  getRawAnswer,
  getSpecificRawAnswer,
  indexSettingsName,
} from 'Helpers/SettingsHelper'

export interface Question {
  settingsName: string
  code: string
  name: string
  translationCode: string
  defaultValue: string
  disablingReason: string
  // writePermissions: { Role.SuperAdmin, Role.Admin, Role.User, Role.Guest }
  typeOfAnswer: string
  possibleValues: PossibleValues
  enabled: boolean
  isIdQuestion: boolean
  isMultiAnswers: boolean
  alreadyRespondedValues: any
  onlyDistinctValues: boolean
  isAllEnabled: boolean
  isOptional: boolean
}

export interface PossibleValue {
  value: string
  source: string
  extraFields?: Record<string, string>
}

export interface PossibleValues {
  [Key: string]: PossibleValue
}

export interface QuestionGroup {
  id: SettingsName
  name: string
  translationCode: string
  questions: Question[]
  questionGroups: QuestionGroup[]
  priority: number
  isVisible: boolean
}

export interface SettingsGroups {
  [Key: string]: QuestionGroup
}

export interface SettingsResponse {
  [Key: string]: RawAnswer[] | RespondingEntity[]
}

//All settings values from database to match responding entities and default column
export interface RawAnswer {
  settingsName: string
  questionCode: string
  respondingEntityId: string
  answer: string
  priority: number
}

//Entities that reflect columns of the settings, no columns usually has only default column
export interface RespondingEntity {
  id: string
  name: string
  description: string
  isDeleted: boolean
  canBeUpdated: boolean
  canBeDeleted: boolean
}

export interface AnswerProp {
  value: string
  priority: number
}

export const settingsSlice = createSlice({
  name: 'settings',
  initialState: {
    settings: {} as SettingsGroups,
    answers: {} as { [Key: string]: SettingsResponse },
    editedAnswers: {} as { [Key: string]: RawAnswer[] },
    deletedAnswers: {} as { [Key: string]: RawAnswer[] },
    changeModal: false,
    isLoading: false,
  },
  reducers: {
    setSettings: (state, action) => {
      state.settings = action.payload
    },
    setSpecificSetting: (state, action) => {
      const hasQuestion = Object.keys(state.settings).find((key: string) => {
        return key === action.payload.settingsName
      })

      if (hasQuestion) {
        const questionCopy = { ...state.settings }
        questionCopy[action.payload.settingsName] = action.payload.data
        state.settings = questionCopy
      }
    },
    setAnswers: (state, action) => {
      const hasAnswers = Object.keys(state.answers).find((key: string) => {
        return key === action.payload.settingsName
      })

      if (hasAnswers) {
        const answersCopy = { ...state.answers }
        answersCopy[action.payload.settingsName] = action.payload.data
        state.answers = answersCopy
      } else {
        state.answers = { ...state.answers, [action.payload.settingsName]: action.payload.data }
      }
    },
    editAnswer: (state, action) => {
      const hasAnswersEdit = state.editedAnswers.hasOwnProperty(action.payload.settingsName)

      if (hasAnswersEdit) {
        const editAnswersCopy = { ...state.editedAnswers }
        const editRawAnswers = editAnswersCopy[action.payload.settingsName] ?? []

        const isAnswerStored = getRawAnswer(
          action.payload.respondingEntityId,
          action.payload.questionCode,
          editRawAnswers,
        )

        if (isAnswerStored) {
          const filteredAnswer = editRawAnswers.filter((x: RawAnswer) => {
            return (
              x.questionCode !== action.payload.questionCode ||
              (x.questionCode === action.payload.questionCode &&
                x.respondingEntityId !== action.payload.respondingEntityId)
            )
          })

          if (action.payload.isMultiAnswers) {
            editAnswersCopy[action.payload.settingsName] = filteredAnswer.concat(action.payload.rawAnswer)
          } else {
            filteredAnswer.push(action.payload.rawAnswer)
            editAnswersCopy[action.payload.settingsName] = filteredAnswer
          }

          state.editedAnswers = editAnswersCopy
        } else {
          if (action.payload.isMultiAnswers) {
            editAnswersCopy[action.payload.settingsName] = editRawAnswers.concat(action.payload.rawAnswer)
          } else {
            editRawAnswers.push(action.payload.rawAnswer)
            editAnswersCopy[action.payload.settingsName] = editRawAnswers
          }

          state.editedAnswers = editAnswersCopy
        }
      } else {
        const answers = action.payload.isMultiAnswers ? action.payload.rawAnswer : [action.payload.rawAnswer]
        state.editedAnswers = { ...state.editedAnswers, [action.payload.settingsName]: answers }
      }
    },
    deleteAnswer: (state, action) => {
      const { settingsName, editInfo } = action.payload
      const hasAnswersDelete = state.deletedAnswers.hasOwnProperty(settingsName)

      if (editInfo?.editType === 'delete') {
        const answersCopy = { ...state.answers }
        const rawAnswers = answersCopy[settingsName]?.rawAnswers || []
        //add selectedAnswer to the list of deletedAnswers ONLY if it's already stored which means it's actually deletable
        if (rawAnswers.length > 0) {
          if (editInfo.selectedAnswer.answer === 'empty') {
            const answersToDelete = generateRawAnswerFromEmptyValue(
              editInfo.selectedAnswer.respondingEntityId,
              editInfo.selectedAnswer.questionCode,
              rawAnswers as RawAnswer[],
              editInfo.selectedAnswer.settingsName,
            )

            const updatedDeletedAnswers = hasAnswersDelete
              ? { ...state.deletedAnswers }
              : { ...state.deletedAnswers, [settingsName]: [] }
            updatedDeletedAnswers[settingsName] = updatedDeletedAnswers[settingsName].concat(answersToDelete)
            if (updatedDeletedAnswers[settingsName].length === 0) {
              delete updatedDeletedAnswers[settingsName]
            }
            state.deletedAnswers = updatedDeletedAnswers
          } else {
            const isAnswerDeletable = getSpecificRawAnswer(
              editInfo.selectedAnswer.respondingEntityId,
              editInfo.selectedAnswer.questionCode,
              rawAnswers as RawAnswer[],
              editInfo.selectedAnswer.answer,
            )
            if (isAnswerDeletable) {
              const updatedDeletedAnswers = hasAnswersDelete
                ? { ...state.deletedAnswers }
                : { ...state.deletedAnswers, [settingsName]: [] }

              updatedDeletedAnswers[settingsName].push(editInfo.selectedAnswer)

              state.deletedAnswers = updatedDeletedAnswers
            }
          }
        }
      } else if (editInfo?.editType === 'add') {
        const answersCopy = { ...state.deletedAnswers }
        if (hasAnswersDelete) {
          const specificRawAnswer = getSpecificRawAnswer(
            editInfo.selectedAnswer.respondingEntityId,
            editInfo.selectedAnswer.questionCode,
            answersCopy[settingsName],
            editInfo.selectedAnswer.answer,
          )
          //if the added answer is in the state of deletedAnswers, remove it from there
          if (specificRawAnswer) {
            answersCopy[settingsName] = answersCopy[settingsName].filter(x => x !== specificRawAnswer)
            if (answersCopy[settingsName].length === 0) {
              //if state.deletedAnswers[settingsName] remains empty, delete the state in order to avoid an empty payload
              delete state.deletedAnswers[settingsName]
            } else {
              state.deletedAnswers = answersCopy
            }
          }
        }
      }
    },
    resetEdit: state => {
      state.editedAnswers = {}
      state.deletedAnswers = {}
    },
    removeEmptyValues: state => {
      const editedAnswersCopy = { ...state.editedAnswers }
      Object.entries(editedAnswersCopy).forEach(([key, value]: [string, RawAnswer[]]) => {
        editedAnswersCopy[key] = editedAnswersCopy[key].filter(x => x.answer !== SettingsAnswerEmptyValue)
        if (editedAnswersCopy[key].length === 0) {
          delete editedAnswersCopy[key]
        }
      })
      state.editedAnswers = editedAnswersCopy
    },
    toggleChangeModal: (state, action) => {
      state.changeModal = action.payload
    },
    setRespondingEntity: (state, action) => {
      const hasAnswers = Object.keys(state.answers).find((key: string) => {
        return key === action.payload.settingsName
      })

      if (hasAnswers) {
        const answersCopy = { ...state.answers }
        const selectedAnswerCopy = answersCopy[action.payload.settingsName]
        selectedAnswerCopy.respondingEntities.push(action.payload.data)
        state.answers[action.payload.settingsName] = selectedAnswerCopy
      }
    },
    setSettingsNames: (state, action) => {
      const indexedSettingsName = indexSettingsName(action.payload)
      state.settings = indexedSettingsName
    },
    setSettingLoading: (state, action) => {
      state.isLoading = action.payload
    },
  },
})

// Action creators are generated for each case reducer function
export const {
  setSettings,
  setAnswers,
  editAnswer,
  resetEdit,
  toggleChangeModal,
  setRespondingEntity,
  setSpecificSetting,
  setSettingsNames,
  setSettingLoading,
  deleteAnswer,
  removeEmptyValues,
  // getSettingsToUpdate,
} = settingsSlice.actions

export default settingsSlice.reducer
